import { TranslationKeys } from "translations";

interface ApprovalPayoutInput {
  customerPaysOut: boolean;
  /** The month's payday. */
  regularPayoutDate: Date;
  /** When Wint's payout would land if approved now, which slips past payday once its bank lead time no longer fits. */
  effectivePayoutDate: Date;
  /** Wint's approval deadline: the last day its bank lead time still makes payday. */
  latestApprovalDate: Date;
  today: Date;
}

/**
 * What the approval card says about the regular salary payout, and when. A self-paying customer pays on payday
 * whatever Wint's own lead time is, so Wint's late-approval date and wording never apply to it. Early payouts on
 * their own dates are always Wint's and keep their own wording.
 */
export const approvalPayoutView = ({
  customerPaysOut,
  regularPayoutDate,
  effectivePayoutDate,
  latestApprovalDate,
  today,
}: ApprovalPayoutInput) => {
  if (!customerPaysOut) {
    return {
      header: TranslationKeys.Salary.WintTodoHeader,
      description: TranslationKeys.Salary.WintTodoDescription,
      payoutDateLabel: TranslationKeys.SalaryDeviation.WintTodoPayoutDate,
      payoutDateLabelCapitalized: TranslationKeys.SalaryDeviation.WintTodoPayoutDateCapitalized,
      payoutDate: effectivePayoutDate,
      payAsSoonAsPossible: false,
      isLate: effectivePayoutDate.getTime() > regularPayoutDate.getTime(),
      outstandingRowLabel: undefined,
      approveBy: latestApprovalDate as Date | undefined,
    };
  }

  const endOfPayday = new Date(regularPayoutDate);
  endOfPayday.setHours(23, 59, 59, 999);

  const payAsSoonAsPossible = today.getTime() > endOfPayday.getTime();

  return {
    header: TranslationKeys.Salary.WintTodoHeaderCustomerPaysOut,
    description: TranslationKeys.Salary.WintTodoDescriptionCustomerPaysOut,
    payoutDateLabel: TranslationKeys.SalaryDeviation.WintTodoCustomerPayoutDate,
    payoutDateLabelCapitalized: TranslationKeys.SalaryDeviation.WintTodoCustomerPayoutDateCapitalized,
    payoutDate: regularPayoutDate,
    payAsSoonAsPossible,
    isLate: false,
    outstandingRowLabel: payAsSoonAsPossible
      ? TranslationKeys.SalaryDeviation.UserPayoutCustomerPaysAsSoonAsPossible
      : TranslationKeys.SalaryDeviation.UserPayoutCustomerPaysOnDate,
    // Wint's lead time is irrelevant when the customer pays: approve by payday, and once that has passed, at once.
    approveBy: payAsSoonAsPossible ? undefined : regularPayoutDate,
  };
};
